import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { differenceInSeconds } from 'date-fns';
import { TFunction } from 'react-i18next';
import { ExtendedComponentSchema } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { object, string, lazy } from 'yup';
import { AutocompleteOption } from '@dxb-web-ui-framework/design-system';
import { APIGatewayResponseDataHeader } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { isDefined, isEmpty } from '@dxb-web-ui-framework/common/utils';
import { ChargingMethod } from '../domain/ChargingMethod/entities';
import { Country } from '../domain/Country/entities';
import { ForeCastFormData, ValueIdentifier } from '../components/types';
import { RoutingTransferTypes } from '../domain/Transfer/entities/RoutingResponseDTO';
import { Forecast, SwiftBank, SwiftCity, BopCode, VocCode } from '../domain/Transfer/entities';
import { Account } from '../../../account/src/domain/Account/entities';
import { Currency } from '../domain/Currency/entities';
import { Beneficiary } from '../../../beneficiary/src/domain/Beneficiary/entities';
import { FavouriteTransfer } from '../domain/FavouriteTransfer/entities';
import { Priorities } from '../domain/Transfer/entities/ValueDateResponseDTO';
import { COR_REGEX, BIK_REGEX, INN_REGEX, KPP_REGEX } from '../components/TransferStepper/constants';
import { BopCodesIdentifiers } from '../domain/Transfer/entities/BopCode';
import { transformDataToAutocompleteOptions, shouldShowRequestRateBtn } from './utils';

export enum FieldErrorNames {
  receiverComments = 'receiverComments',
  receiverBic = 'receiverBic',
  receiverAddress = 'receiverAddress',
  receiverFullName = 'receiverFullName',
  chargingMethod = 'chargingMethod',
  chargingMethodDescription = 'chargingMethodDescription',
  countrySn = 'countrySn',
  countryDescription = 'countryDescription',
  countryShortDescription = 'countryShortDescription',
  priority = 'priority',
  additionalGlobalOrdersDetailBicBankCode = 'additionalGlobalOrdersDetailBicBankCode',
  additionalGlobalOrdersDetailInnTaxPayerCode = 'additionalGlobalOrdersDetailInnTaxPayerCode',
  additionalGlobalOrdersDetailCorCbr = 'additionalGlobalOrdersDetailCorCbr',
  additionalGlobalOrdersDetailKppBeneficiary = 'additionalGlobalOrdersDetailKppBeneficiary',
  additionalGlobalOrdersDetailVocGhCode = 'additionalGlobalOrdersDetailVocGhCode',
  additionalGlobalOrdersDetailVocGdCode = 'additionalGlobalOrdersDetailVocGdCode',
  additionalGlobalOrdersDetailBopGh = 'additionalGlobalOrdersDetailBopGh',
  additionalGlobalOrdersDetailBopGd = 'additionalGlobalOrdersDetailBopGd',
  receiverSortCode = 'receiverSortCode',
}

const dataToAutocompleteOptions = (
  data: Country[] | SwiftCity[] | SwiftBank[] | BopCode[] | VocCode[] | undefined,
): AutocompleteOption[] | [] => {
  if (!data) {
    return [];
  }
  return data.map((d, index) => {
    if (d instanceof SwiftBank) {
      return {
        id: index,
        value: d.bankName,
        label: d.bankName,
        checked: false,
      };
    }
    if (d instanceof SwiftCity) {
      return {
        id: index,
        value: d.cityName,
        label: d.cityName,
        checked: false,
      };
    }
    return {
      id: d.serialNumber,
      value: d.description,
      label: d.description,
      checked: false,
    };
  });
};

const preloadedErrorsSetter = (errorSetter: Function, fieldResponseErrors?: APIGatewayResponseDataHeader) => {
  const errors: any[] = [];
  if (fieldResponseErrors) {
    [fieldResponseErrors].forEach((error: APIGatewayResponseDataHeader | undefined) => {
      if (!error || !error?.param) {
        return;
      }
      const isChargingMethodError =
        error.param === FieldErrorNames.chargingMethod || error.param === FieldErrorNames.chargingMethodDescription;
      const isCountryError =
        error.param === FieldErrorNames.countryDescription ||
        error.param === FieldErrorNames.countryShortDescription ||
        error.param === FieldErrorNames.countrySn;
      const fieldIdentifier = isChargingMethodError
        ? ValueIdentifier.CHARGING_METHOD
        : isCountryError
        ? ValueIdentifier.BANK_COUNTRY
        : error.param;

      errors.push({ param: fieldIdentifier, message: error.message });
    });
    errorSetter(errors);
  }
};

const maskWordsOfSentence = (sentence: string): string => {
  const words = sentence.split(' ');
  const maskedSentence: string[] = [];

  words.forEach(word => {
    let maskedRestWordLetters: string[] = [];
    const startingLetter = word[0];
    const restWordLetters = word.substring(1, word.length);
    for (const char of restWordLetters) {
      maskedRestWordLetters.push(char.replace(char, '*'));
    }
    maskedSentence.push(`${startingLetter}${maskedRestWordLetters.join('')}`);
  });
  return maskedSentence.join(' ');
};

const creditAccountNumberValueDefiner = (
  creditAccountNumber: string,
  accounts: Account[] | null,
  beneficiaries: Beneficiary[] | null,
) => {
  if (!accounts || !beneficiaries) {
    return creditAccountNumber;
  }

  const accountFound = accounts?.find(
    account =>
      account.alias === creditAccountNumber ||
      account.accountNumber === creditAccountNumber ||
      account.iban === creditAccountNumber,
  );

  const beneficiaryFound = beneficiaries?.find(
    ben => ben.name === creditAccountNumber || ben.iban === creditAccountNumber,
  );

  return accountFound?.iban || beneficiaryFound?.iban || creditAccountNumber;
};

const defaultRadioPriorityValue = (
  data: GenericObject,
  radioName: string,
  formData: Nullable<ForeCastFormData>,
  isSepaRoutingSystem?: boolean,
  favouriteTransferPriority?: string | null,
) => {
  if (favouriteTransferPriority) {
    return radioName === favouriteTransferPriority.toLowerCase() ? favouriteTransferPriority.toLowerCase() : '';
  }

  if (radioName === ValueIdentifier.NORMAL) {
    if (isSepaRoutingSystem) {
      return '';
    }

    if (!data?.forecastFormData?.priority && (!formData?.priority || formData?.priority === radioName)) {
      return ValueIdentifier.NORMAL;
    }

    if (data?.forecastFormData?.priority === radioName) {
      return ValueIdentifier.NORMAL;
    }

    if (data?.forecastFormData?.priority === ValueIdentifier.IMMEDIATE) {
      return '';
    }

    if (formData?.priority === radioName) {
      return ValueIdentifier.NORMAL;
    }

    return '';
  }

  if (data?.forecastFormData?.priority === radioName) {
    return ValueIdentifier.IMMEDIATE;
  }

  if (data?.forecastFormData?.priority === ValueIdentifier.NORMAL) {
    return '';
  }

  if (formData?.priority === radioName) {
    return ValueIdentifier.IMMEDIATE;
  }

  if (isSepaRoutingSystem && radioName === ValueIdentifier.IMMEDIATE) {
    return ValueIdentifier.IMMEDIATE;
  }

  return '';
};

const transactionUILabelValuesEnabler = (
  transferType: Nullable<RoutingTransferTypes>,
): {
  shouldShowCharges: boolean;
  shouldShowCommissions: boolean;
  shouldShowExpenses: boolean;
  shouldShowTaxes: boolean;
  shouldShowBankName: boolean;
  shouldShowBankBIC: boolean;
  shouldShowBeneficiaryCountry: boolean;
  shouldShowBeneficiaryAddress: boolean;
  shouldShowExchangeRates: boolean;
} => ({
  shouldShowCharges:
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY,
  shouldShowCommissions:
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY,
  shouldShowExpenses:
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY,
  shouldShowTaxes:
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY,
  shouldShowBankName:
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.INTRABANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.INTRABANK_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY,
  shouldShowBankBIC:
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.INTRABANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.INTRABANK_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY,
  shouldShowBeneficiaryCountry:
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY,
  shouldShowBeneficiaryAddress:
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY,
  shouldShowExchangeRates:
    transferType === RoutingTransferTypes.INTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.INTRABANK_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY,
});

const chargingMethodsFormComponentsConfig = (
  isLoading: boolean,
  chargingMethods: ChargingMethod[],
  data: GenericObject,
  formData: Nullable<ForeCastFormData>,
  favouriteTransfer?: Nullable<FavouriteTransfer>,
) =>
  chargingMethods
    .filter(cm => cm.serialNumber !== 0)
    .map(cm => {
      if (formData && formData?.chargingMethod && Number(formData?.chargingMethod) === cm.serialNumber) {
        return {
          type: 'radio',
          label: cm.description.toUpperCase(),
          key: String(cm.serialNumber),
          validateOn: 'onChange',
          disabled: isLoading,
          defaultValue: formData?.chargingMethod,
          properties: {
            shouldGroupValidation: true,
          },
        };
      }

      if (
        data?.forecastFormData?.chargingMethod &&
        Number(data?.forecastFormData?.chargingMethod) === cm.serialNumber
      ) {
        return {
          type: 'radio',
          label: cm.description.toUpperCase(),
          key: String(cm.serialNumber),
          validateOn: 'onChange',
          disabled: isLoading,
          defaultValue: data.forecastFormData.chargingMethod,
          properties: {
            shouldGroupValidation: true,
          },
        };
      }

      if (
        !data?.forecastFormData?.chargingMethod &&
        favouriteTransfer?.chargingMethod &&
        Number(favouriteTransfer?.chargingMethod) === cm.serialNumber
      ) {
        return {
          type: 'radio',
          label: cm.description.toUpperCase(),
          key: String(cm.serialNumber),
          validateOn: 'onChange',
          disabled: isLoading,
          defaultValue: favouriteTransfer?.chargingMethod,
          properties: {
            shouldGroupValidation: true,
          },
        };
      }

      if (
        cm.serialNumber === 3 &&
        !formData?.chargingMethod &&
        !data?.forecastFormData?.chargingMethod &&
        !favouriteTransfer?.chargingMethod
      ) {
        return {
          type: 'radio',
          label: cm.description.toUpperCase(),
          key: String(cm.serialNumber),
          validateOn: 'onChange',
          disabled: isLoading,
          defaultValue: cm.description,
          properties: {
            shouldGroupValidation: true,
          },
        };
      }

      return {
        type: 'radio',
        label: cm.description.toUpperCase(),
        key: String(cm.serialNumber),
        validateOn: 'onChange',
        disabled: isLoading,
        defaultValue: '',
        properties: {
          shouldGroupValidation: true,
        },
      };
    });

const getBopCodeByCountryId = (countryId: number) => {
  let bopCode = undefined;
  switch (countryId) {
    case 74:
      bopCode = BopCodesIdentifiers.POPUK;
      break;
    case 76:
      bopCode = BopCodesIdentifiers.BOPIN;
      break;
    case 78:
      bopCode = BopCodesIdentifiers.BOPJO;
      break;
    case 68:
      bopCode = BopCodesIdentifiers.BOPUA;
      break;
    default:
      break;
  }
  return bopCode;
};

const constructForecastRequestObjectByTransferType = (
  bankName: string,
  priorityDate: Priorities,
  orderValueDate: string,
  stepValues: GenericObject,
  formData: ForeCastFormData,
  bankCountry: Country,
  beneficiaryCountry: Country,
  chargingMethods: ChargingMethod[],
  defaultVocShortCodeDescription?: string,
  routingSystem?: 'SEPA' | 'Swift',
): Forecast & {
  orderValueDate?: string;
  receiverSortCode?: string;
  priorityDate: Priorities;
  bankName: Nullable<string>;
  receiverCity?: string;
  routingSystem?: 'SEPA' | 'Swift';
} => {
  const chargingMethod = chargingMethods.find(chargingMethod => {
    const transferType = stepValues.routingResponseData.transferType;
    if (
      transferType === RoutingTransferTypes.INTRABANK_SAME_CURRENCY ||
      transferType === RoutingTransferTypes.INTERNAL_SAME_CURRENCY ||
      transferType === RoutingTransferTypes.CORPORATE_INTERNAL_SAME_CURRENCY ||
      transferType === RoutingTransferTypes.INTERNAL_OTHER_CURRENCY ||
      transferType === RoutingTransferTypes.INTRABANK_OTHER_CURRENCY ||
      transferType === RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY
    ) {
      // 3 stands for SHA/SLEV and this only applies to INTRABANK & INTERNAL
      return chargingMethod.serialNumber === 3;
    }
    return chargingMethod.serialNumber === Number(formData.chargingMethod);
  }) as ChargingMethod;

  const bobCode = bankCountry && getBopCodeByCountryId(bankCountry?.serialNumber);
  const isRuCurrency = stepValues?.otherMandatoryDataForUI?.currency?.serialNumber === 25;

  switch (stepValues.routingResponseData.transferType) {
    case RoutingTransferTypes.INTERNAL_OTHER_CURRENCY:
      return {
        appTransactionUniqueId: stepValues.routingResponseData.appTransactionId,
        receiverComments: formData.receiverComments,
        receiverBic: stepValues.routingResponseData.bic,
        receiverAddress: stepValues.routingResponseData.bankAddress,
        receiverFullName: stepValues.routingResponseData.creditorFullName,
        chargingMethod: chargingMethod.serialNumber,
        chargingMethodDescription: chargingMethod.description,
        countrySn: beneficiaryCountry?.serialNumber,
        bankCountrySn: bankCountry?.serialNumber,
        additionalGlobalOrdersDetailVocGhCode: formData?.vo?.id
          ? isRuCurrency
            ? 'RUVOC'
            : defaultVocShortCodeDescription
          : undefined,
        additionalGlobalOrdersDetailVocGdCode: formData?.vo?.id,
        priorityDate,
        orderValueDate,
        bankName,
        receiverCity: stepValues.routingResponseData?.bankCity,
        routingSystem,
      };
    case RoutingTransferTypes.INTRABANK_SAME_CURRENCY:
    case RoutingTransferTypes.INTRABANK_OTHER_CURRENCY:
    case RoutingTransferTypes.CORPORATE_INTERNAL_SAME_CURRENCY:
    case RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY:
      return {
        appTransactionUniqueId: stepValues.routingResponseData.appTransactionId,
        receiverComments: formData.receiverComments,
        receiverBic: stepValues.routingResponseData.bic,
        receiverAddress: stepValues.routingResponseData.bankAddress,
        receiverFullName: stepValues.routingResponseData.creditorFullName,
        chargingMethod: chargingMethod.serialNumber,
        chargingMethodDescription: chargingMethod.description,
        countrySn: beneficiaryCountry?.serialNumber,
        bankCountrySn: bankCountry?.serialNumber,
        receiverSortCode: formData?.receiverSortCode,
        additionalGlobalOrdersDetailVocGhCode: formData?.vo?.id
          ? isRuCurrency
            ? 'RUVOC'
            : defaultVocShortCodeDescription
          : undefined,
        additionalGlobalOrdersDetailVocGdCode: formData?.vo?.id,
        priorityDate,
        orderValueDate,
        bankName,
        receiverCity: stepValues.routingResponseData?.bankCity,
        routingSystem,
      };
    case RoutingTransferTypes.EXTERNAL_SAME_CURRENCY:
    case RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY:
      return {
        appTransactionUniqueId: stepValues.routingResponseData.appTransactionId,
        receiverComments: formData.receiverComments,
        receiverBic: stepValues.routingResponseData.bic || formData?.receiverBic,
        receiverAddress: formData.receiverAddress as string,
        receiverFullName: formData.receiverFullName as string,
        chargingMethod: chargingMethod.serialNumber,
        chargingMethodDescription: chargingMethod.description,
        countrySn: beneficiaryCountry?.serialNumber,
        bankCountrySn: bankCountry?.serialNumber,
        additionalGlobalOrdersDetailBicBankCode: formData?.bik,
        additionalGlobalOrdersDetailInnTaxPayerCode: formData?.inn,
        additionalGlobalOrdersDetailCorCbr: formData?.cor,
        additionalGlobalOrdersDetailKppBeneficiary: formData?.kpp,
        additionalGlobalOrdersDetailVocGhCode: formData?.vo?.id
          ? isRuCurrency
            ? 'RUVOC'
            : defaultVocShortCodeDescription
          : undefined,
        additionalGlobalOrdersDetailVocGdCode: formData?.vo?.id,
        additionalGlobalOrdersDetailBopGh: bobCode,
        additionalGlobalOrdersDetailBopGd: formData?.bopCode?.id,
        receiverSortCode: formData?.receiverSortCode,
        priorityDate,
        orderValueDate,
        bankName,
        receiverCity: formData?.receiverCity,
        routingSystem,
      };

    case RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY:
    case RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY:
      return {
        appTransactionUniqueId: stepValues.routingResponseData.appTransactionId,
        receiverComments: formData.receiverComments,
        receiverBic: formData?.receiverBic as string,
        receiverAddress: formData?.receiverAddress as string,
        receiverFullName: formData?.receiverFullName as string,
        chargingMethod: chargingMethod.serialNumber,
        chargingMethodDescription: chargingMethod.description,
        countrySn: beneficiaryCountry?.serialNumber,
        bankCountrySn: bankCountry?.serialNumber,
        additionalGlobalOrdersDetailBicBankCode: formData?.bik,
        additionalGlobalOrdersDetailInnTaxPayerCode: formData?.inn,
        additionalGlobalOrdersDetailCorCbr: formData?.cor,
        additionalGlobalOrdersDetailKppBeneficiary: formData?.kpp,
        additionalGlobalOrdersDetailVocGhCode: formData?.vo?.id
          ? isRuCurrency
            ? 'RUVOC'
            : defaultVocShortCodeDescription
          : undefined,
        additionalGlobalOrdersDetailVocGdCode: formData?.vo?.id,
        additionalGlobalOrdersDetailBopGh: bobCode,
        additionalGlobalOrdersDetailBopGd: formData?.bopCode?.id,
        receiverSortCode: formData?.receiverSortCode,
        priorityDate,
        orderValueDate,
        bankName,
        receiverCity: formData?.receiverCity,
        routingSystem,
      };
    default:
      return {
        appTransactionUniqueId: stepValues.routingResponseData.appTransactionId,
        receiverComments: formData.receiverComments,
        receiverBic: stepValues.routingResponseData.bic,
        receiverAddress: stepValues.routingResponseData.bankAddress,
        receiverFullName: stepValues.routingResponseData.creditorFullName,
        chargingMethod: chargingMethod.serialNumber,
        chargingMethodDescription: chargingMethod.description,
        countrySn: beneficiaryCountry?.serialNumber,
        bankCountrySn: bankCountry?.serialNumber,
        priorityDate,
        orderValueDate,
        bankName,
        receiverCity: stepValues.routingResponseData?.bankCity,
        routingSystem,
      };
  }
};

const specialCaseFormFieldConfigs = (
  t: TFunction,
  transferType: RoutingTransferTypes,
  countries: Country[],
  currencies: Currency[],
  hidden: boolean,
  isLoading: boolean,
  selectedCurrency: string,
  selectedCountry?: string | null,
  routingResponseSortCode?: string,
  bopCodes?: BopCode[],
  isLoadingBopCodes?: boolean,
  vocCodes?: VocCode[],
  isLoadingVocCodes?: boolean,
): ExtendedComponentSchema<any>[] => {
  const country = countries
    .filter(c => c.description !== '')
    .find(c => c.serialNumber === Number(selectedCountry) || c.description === selectedCountry);
  const currency = currencies.filter(c => c.description !== '').find(c => c.serialNumber === Number(selectedCurrency));

  // special cases
  const isUKCountry = country?.serialNumber === 74;
  const isJordanCountry = country?.serialNumber === 78;
  const isIndiaCountry = country?.serialNumber === 76;
  const isUAECountry = country?.serialNumber === 68;
  const isRUCountry = country?.serialNumber === 199;
  const isGBPCurrency = currency?.serialNumber === 4;
  const isRuCurrency = currency?.serialNumber === 25;

  // transfer types
  const isExternalUnknownBank =
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY;
  const isExternal =
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY;

  // generic field hidden to return if there is no matching special case
  const genericHiddenFieldConfig = [
    {
      label: '',
      key: '',
      type: 'text',
      defaultValue: '',
      properties: {
        customLogic: {
          hidden: () => true,
        },
      },
    },
  ];

  // helper to determine if a special field is hidden
  const propertiesLogic: any = () => {
    if (isExternalUnknownBank) {
      return {
        customLogic: {
          hidden: () => hidden,
        },
      };
    }
    return {};
  };
  // field Configs by transfer type
  if (isExternalUnknownBank || isExternal) {
    //Making transfer to UK country with GPB currency
    if (isUKCountry && isGBPCurrency && isDefined(routingResponseSortCode) && routingResponseSortCode !== '') {
      return [
        {
          label: t('fundsTransferPage.Forms.SortCode'),
          key: ValueIdentifier.RECEIVER_SORT_CODE,
          type: 'text',
          disabled: true,
          defaultValue: '',
          properties: propertiesLogic(),
        },
        {
          label: t('fundsTransferPage.Forms.BopCode'),
          key: ValueIdentifier.BOP_CODE,
          type: 'autocomplete',
          options: dataToAutocompleteOptions(bopCodes),
          defaultValue: null,
          disabled: isLoading,
          loading: !!isLoadingBopCodes,
          loadingText: t('fundsTransferPage.Forms.Loading.BopCode.Message'),
          properties: Object.assign(
            {
              mt: 3,
              forcePopupIcons: true,
              noOptionsText: t('fundsTransferPage.Forms.NoOptions.BopCode.Message'),
            },
            propertiesLogic(),
          ),
        },
      ];
    }
    //Making transfer to Jordan || India || UAE
    if (isJordanCountry || isIndiaCountry || isUAECountry || (isUKCountry && isGBPCurrency)) {
      if (isRuCurrency) {
        return [
          {
            label: t('fundsTransferPage.Forms.VO'),
            key: ValueIdentifier.VO,
            type: 'autocomplete',
            options: dataToAutocompleteOptions(vocCodes?.filter(vc => vc.serialNumber !== 0)),
            defaultValue: null,
            disabled: isLoading,
            loading: !!isLoadingVocCodes,
            loadingText: t('fundsTransferPage.Forms.Loading.VocCode.Message'),
            properties: Object.assign(
              {
                mt: 3,
                forcePopupIcons: true,
                noOptionsText: t('fundsTransferPage.Forms.NoOptions.VocCode.Message'),
              },
              propertiesLogic(),
            ),
          },
          {
            label: t('fundsTransferPage.Forms.BopCode'),
            key: ValueIdentifier.BOP_CODE,
            type: 'autocomplete',
            options: dataToAutocompleteOptions(bopCodes),
            defaultValue: null,
            disabled: isLoading,
            loading: !!isLoadingBopCodes,
            loadingText: t('fundsTransferPage.Forms.Loading.BopCode.Message'),
            properties: Object.assign(
              {
                mt: 3,
                forcePopupIcons: true,
                noOptionsText: t('fundsTransferPage.Forms.NoOptions.BopCode.Message'),
              },
              propertiesLogic(),
            ),
          },
        ];
      } else {
        return [
          {
            label: t('fundsTransferPage.Forms.BopCode'),
            key: ValueIdentifier.BOP_CODE,
            type: 'autocomplete',
            options: dataToAutocompleteOptions(bopCodes),
            defaultValue: null,
            disabled: isLoading,
            loading: !!isLoadingBopCodes,
            loadingText: t('fundsTransferPage.Forms.Loading.BopCode.Message'),
            properties: Object.assign(
              {
                mt: 3,
                forcePopupIcons: true,
                noOptionsText: t('fundsTransferPage.Forms.NoOptions.BopCode.Message'),
              },
              propertiesLogic(),
            ),
          },
        ];
      }
    }
    // Making transfer to RU with RUB
    if (isRUCountry && isRuCurrency) {
      return [
        {
          label: t('fundsTransferPage.Forms.COR'),
          key: ValueIdentifier.COR,
          type: 'text',
          disabled: isLoading,
          defaultValue: '',
          properties: Object.assign(
            {
              shouldCapitalizeValue: true,
            },
            propertiesLogic(),
          ),
        },
        {
          label: t('fundsTransferPage.Forms.BIK'),
          key: ValueIdentifier.BIK,
          type: 'text',
          disabled: isLoading,
          defaultValue: '',
          properties: Object.assign(
            {
              shouldCapitalizeValue: true,
            },
            propertiesLogic(),
          ),
        },
        {
          label: t('fundsTransferPage.Forms.INN'),
          key: ValueIdentifier.INN,
          type: 'text',
          disabled: isLoading,
          defaultValue: '',
          properties: Object.assign(
            {
              shouldCapitalizeValue: true,
            },
            propertiesLogic(),
          ),
        },
        {
          label: t('fundsTransferPage.Forms.KPP'),
          key: ValueIdentifier.KPP,
          type: 'text',
          disabled: isLoading,
          defaultValue: '',
          properties: Object.assign(
            {
              shouldCapitalizeValue: true,
            },
            propertiesLogic(),
          ),
        },
        {
          label: t('fundsTransferPage.Forms.VO'),
          key: ValueIdentifier.VO,
          type: 'autocomplete',
          options: dataToAutocompleteOptions(vocCodes?.filter(vc => vc.serialNumber !== 0)),
          defaultValue: null,
          disabled: isLoading,
          loading: !!isLoadingVocCodes,
          loadingText: t('fundsTransferPage.Forms.Loading.VocCode.Message'),
          properties: Object.assign(
            {
              mt: 3,
              forcePopupIcons: true,
              noOptionsText: t('fundsTransferPage.Forms.NoOptions.VocCode.Message'),
            },
            propertiesLogic(),
          ),
        },
      ];
    }
    // Sending RUB to another country
    if (isRuCurrency) {
      return [
        {
          label: t('fundsTransferPage.Forms.VO'),
          key: ValueIdentifier.VO,
          type: 'autocomplete',
          options: dataToAutocompleteOptions(vocCodes?.filter(vc => vc.serialNumber !== 0)),
          defaultValue: null,
          disabled: isLoading,
          loading: !!isLoadingVocCodes,
          loadingText: t('fundsTransferPage.Forms.Loading.VocCode.Message'),
          properties: Object.assign(
            {
              mt: 3,
              forcePopupIcons: true,
              noOptionsText: t('fundsTransferPage.Forms.NoOptions.VocCode.Message'),
            },
            propertiesLogic(),
          ),
        },
      ];
    }
    return genericHiddenFieldConfig;
  }

  return genericHiddenFieldConfig;
};

const specialFieldKeysValidationsMapper = (key: string, t: TFunction, isLegalEntity?: boolean) => {
  const validations: any = {
    [ValueIdentifier.RECEIVER_SORT_CODE]: string().required(t('fundsTransferPage.Forms.Validation.SortCode')),
    [ValueIdentifier.BOP_CODE]: lazy(value => {
      switch (typeof value) {
        case 'object':
          return object().nullable(true).required(t('fundsTransferPage.Forms.Validation.BopCode'));
        default:
          return string().required(t('fundsTransferPage.Forms.Validation.BopCode'));
      }
    }),
    [ValueIdentifier.COR]: string()
      .matches(COR_REGEX, t('fundsTransferPage.Forms.Validation.CorRegex'))
      .required(t('fundsTransferPage.Forms.Validation.EmptyField')),
    [ValueIdentifier.BIK]: string()
      .matches(BIK_REGEX, t('fundsTransferPage.Forms.Validation.BikRegex'))
      .required(t('fundsTransferPage.Forms.Validation.EmptyField')),
    [ValueIdentifier.KPP]: string().matches(KPP_REGEX, t('fundsTransferPage.Forms.Validation.KppRegex')),
    [ValueIdentifier.INN]: string()
      .matches(INN_REGEX, t('fundsTransferPage.Forms.Validation.Regex'))
      .required(t('fundsTransferPage.Forms.Validation.EmptyField')),
    [ValueIdentifier.VO]: lazy(value => {
      switch (typeof value) {
        case 'object':
          return object().nullable(true).required(t('fundsTransferPage.Forms.Validation.VocCode'));
        default:
          return string().required(t('fundsTransferPage.Forms.Validation.VocCode'));
      }
    }),
  };

  return validations[key];
};

const constructSpecialFieldsValidations = (
  schema: ExtendedComponentSchema<any>[],
  t: TFunction,
  isLegalEntity?: boolean,
) => {
  const validationObject: any = {};

  schema.forEach(({ key }) => {
    if (!key) {
      return;
    }
    validationObject[key] = specialFieldKeysValidationsMapper(key, t, isLegalEntity);
  });

  return validationObject;
};

const constructSpecialFieldsPreloadedData = (schema: ExtendedComponentSchema<any>[], preloadedData: GenericObject) => {
  const preloadedDataObject: any = {};
  let preloadedVocCode: AutocompleteOption[] | undefined;
  let perloadedBopCode: AutocompleteOption[] | undefined;

  if (preloadedData?.favouriteTransfer?.additionalGlobalOrdersDetailVocGdCode && preloadedData?.vocCodes) {
    const selectedVoc = preloadedData?.vocCodes.find(
      (vc: VocCode) =>
        vc.serialNumber === Number(preloadedData?.favouriteTransfer?.additionalGlobalOrdersDetailVocGdCode),
    );
    const preloadedOption = dataToAutocompleteOptions(selectedVoc ? [selectedVoc] : []);

    if (!isEmpty(preloadedOption)) {
      preloadedVocCode = preloadedOption;
    }
  }

  if (preloadedData?.favouriteTransfer?.additionalGlobalOrdersDetailBopGd && preloadedData?.bopCodes) {
    const selectedBoc = preloadedData?.bopCodes.find(
      (vc: BopCode) => vc.serialNumber === Number(preloadedData?.favouriteTransfer?.additionalGlobalOrdersDetailBopGd),
    );
    const preloadedOption = dataToAutocompleteOptions(selectedBoc ? [selectedBoc] : []);

    if (!isEmpty(preloadedOption)) {
      perloadedBopCode = preloadedOption;
    }
  }

  schema.forEach(({ key }) => {
    if (!key) {
      return;
    }
    switch (key) {
      case ValueIdentifier.RECEIVER_SORT_CODE:
        preloadedDataObject[key] =
          preloadedData?.formData?.sortCode ??
          preloadedData.routingResponseData?.sortCode ??
          preloadedData?.favouriteTransfer?.receiverSortCode ??
          '';
        break;
      case ValueIdentifier.BOP_CODE:
        preloadedDataObject[key] =
          preloadedData?.formData?.bopCode ??
          preloadedData?.forecastFormData?.bopCode ??
          (perloadedBopCode && perloadedBopCode[0]) ??
          '';
        break;
      case ValueIdentifier.COR:
        preloadedDataObject[key] =
          preloadedData?.formData?.cor ??
          preloadedData?.forecastFormData?.cor ??
          preloadedData?.favouriteTransfer?.additionalGlobalOrdersDetailCorCbr ??
          '';
        break;
      case ValueIdentifier.BIK:
        preloadedDataObject[key] =
          preloadedData?.formData?.bik ??
          preloadedData?.forecastFormData?.bik ??
          preloadedData?.favouriteTransfer?.additionalGlobalOrdersDetailBicBankCode ??
          '';
        break;
      case ValueIdentifier.KPP:
        preloadedDataObject[key] =
          preloadedData?.formData?.kpp ??
          preloadedData?.forecastFormData?.kpp ??
          preloadedData?.favouriteTransfer?.additionalGlobalOrdersDetailKppBeneficiary ??
          '';
        break;
      case ValueIdentifier.INN:
        preloadedDataObject[key] =
          preloadedData?.formData?.inn ??
          preloadedData?.forecastFormData?.inn ??
          preloadedData?.favouriteTransfer?.additionalGlobalOrdersDetailInnTaxPayerCode ??
          '';
        break;
      case ValueIdentifier.VO:
        preloadedDataObject[key] =
          preloadedData?.formData?.vo ??
          preloadedData?.forecastFormData?.vo ??
          (preloadedVocCode && preloadedVocCode[0]) ??
          '';
        break;
      default:
        preloadedDataObject[key] = '';
        break;
    }
  });

  return preloadedDataObject;
};

const dateTimeZoneConversion = (date: Date, timeZone: string) => {
  const timeZoneDate = new Date(new Date(date).toLocaleString('en-US', { timeZone }));
  timeZoneDate.setMinutes(date.getMinutes());
  timeZoneDate.setSeconds(date.getSeconds());
  return timeZoneDate;
};

const constructDealerRateCountdownTimerDurationInSeconds = (rateTrxDate: string, rateActiveTime: string) => {
  const trxDate = new Date(rateTrxDate);
  const trxCurrentDate = trxDate.getDate();
  // The timezone was set to Athens because the Profits (backend) will be always in Athens
  const currentDate = dateTimeZoneConversion(new Date(), 'Europe/Athens');
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const currentDay = currentDate.getDate();

  if (trxCurrentDate !== currentDay) {
    return 0;
  }

  const rateActiveTimeDate = new Date(rateActiveTime);
  const rateActiveTimeHours = rateActiveTimeDate.getHours();
  const rateActiveTimeMinutes = rateActiveTimeDate.getMinutes();
  const rateActiveTimeSeconds = rateActiveTimeDate.getSeconds();
  const rateActiveTimeMilliseconds = rateActiveTimeDate.getMilliseconds();

  const calculatedDurationDate = new Date(
    currentYear,
    currentMonth,
    trxCurrentDate,
    rateActiveTimeHours,
    rateActiveTimeMinutes,
    rateActiveTimeSeconds,
    rateActiveTimeMilliseconds,
  );

  const calculatedSeconds = differenceInSeconds(calculatedDurationDate, currentDate);
  return calculatedSeconds - 10;
};

const selectedCountryPredicator = (county?: Country, shouldDisableField: boolean = false) => {
  if (county) {
    return { countryFound: dataToAutocompleteOptions([county])[0], isCountryAutoCompleteDisabled: shouldDisableField };
  }
  return { countryFound: null, isCountryAutoCompleteDisabled: false };
};

const selectedCountryByCountryValueTypePredicator = (value: string | AutocompleteOption, countries: Country[]) => {
  if (typeof value === 'string') {
    return selectedCountryPredicator(countries.find(c => c.description.toLowerCase() === value.toLowerCase()));
  } else {
    return selectedCountryPredicator(countries.find(c => c.serialNumber === (value as AutocompleteOption).id));
  }
};

export {
  selectedCountryPredicator,
  selectedCountryByCountryValueTypePredicator,
  constructForecastRequestObjectByTransferType,
  maskWordsOfSentence,
  creditAccountNumberValueDefiner,
  defaultRadioPriorityValue,
  chargingMethodsFormComponentsConfig,
  transactionUILabelValuesEnabler,
  preloadedErrorsSetter,
  transformDataToAutocompleteOptions,
  dataToAutocompleteOptions,
  specialCaseFormFieldConfigs,
  specialFieldKeysValidationsMapper,
  constructSpecialFieldsValidations,
  constructSpecialFieldsPreloadedData,
  constructDealerRateCountdownTimerDurationInSeconds,
  shouldShowRequestRateBtn,
};
